import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import Dialog, { DialogActionBar } from 'src/components/common/Dialog.redesign';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';
import LeaseInvoice from 'src/models/dlcLease/LeaseInvoice';
import LeaseMaster from 'src/models/dlcLease/LeaseMaster';
import LeaseRecording from 'src/models/dlcLease/LeaseRecording';
import RecordingDetail from 'src/models/dlcLease/RecordingDetail';
import ContractGenerationListDto from 'src/models/leasing/ContractGenerationListDto';
import { IRootState } from 'src/reducers';
import useFetch from 'use-http';

interface ILeaseRecordDialogStateProps {
    leaseNumber: number;
    leaseRecording: LeaseRecording;

    sendUpdate: () => void;
    onCloseDialog?: () => void;
}

const LeaseRecordDialog = (props: ILeaseRecordDialogStateProps) => {
    const appContext = useSelector((state: IRootState) => state.appContext);
    const { register, errors, reset, handleSubmit, getValues, setValue, watch } = useForm();
    const { get, post, put, response } = useFetch();

    const [leaseMaster, setLeaseMaster] = useState<LeaseMaster>(undefined);
    const [leaseInfo, setLeaseInfo] = useState<ContractGenerationListDto>(undefined);
    const [invoices, setInvoices] = useState<LeaseInvoice[]>(undefined);
    const [invoiceOptions, setInvoiceOptions] = useState<{ value: number; label: string; }[]>([{ value: 0, label: 'Create New' }]);
    const [recordingFormData, setRecordingFormData] = useState<LeaseRecording>(undefined);
    const [stateOptions, setStateOptions] = useState<{ value: string; label: string; }[]>([]);
    const [countiesMap, setCountiesMap] = useState<Record<string, { value: string; label: string; }[]>>({});
    const selectedState = watch('state') || getValues()?.state || recordingFormData?.state || '';
    
    useEffect(() => {
        if (props.leaseRecording?.lr_tg > 0) {
            setRecordingFormData(props.leaseRecording);
        } else {
            setRecordingFormData(new LeaseRecording());
        }
        if (props.leaseNumber > 0) {
            getLeaseMaster();
            getInvoices();
            getLeaseInfo();
        }
        getStates();
    }, []);

    useEffect(() => {
        // Need to make sure invoices have loaded.
        if (leaseMaster !== undefined && invoices !== undefined && leaseInfo !== undefined) {
            let initFormData = recordingFormData;
            if (!initFormData.lease_lessor) {
                initFormData.lease_lessor = leaseMaster.lessor;
            }
            if (!initFormData.recording_detail) {
                let detail = new RecordingDetail();
                detail.short_legal = leaseInfo.shortLegal;

                initFormData.recording_detail = detail;
            }

            reset(initFormData);

            if (initFormData.state) {
                getCounties(initFormData.state);
            }
        }
    }, [leaseMaster, leaseInfo, invoices]);

    useEffect(() => {
        if (invoices?.length > 0) {
            let invoiceSelect = [{ value: 0, label: '---' }];
            invoiceSelect = invoiceSelect.concat(invoices.map(io => { return { value: io.li_tg, label: LeaseInvoice.fullNumber(io) }; }));
            setInvoiceOptions(invoiceSelect);
        }
    }, [invoices]);

    const getLeaseMaster = async () => {
        let result = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/processingMaintenance/lease/${props.leaseNumber}`);

        if (response.ok) {
            if (result !== null && result !== '') {
                setLeaseMaster(result);
            } else {
                createLeaseMaster();
            }
        } else {
            console.log('getLeaseMaster Error:', result);
            window.alert('Error getting Lease');
        }
    };

    const getLeaseInfo = async () => {
        let result = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leases/by-number/${props.leaseNumber}`);

        if (response.ok) {
            setLeaseInfo(result);
        } else {
            console.log('getLeaseMaster Error:', result);
            window.alert('Error getting Lease');
        }
    };

    const getInvoices = async () => {
        let result = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/processingMaintenance/lease/${props.leaseNumber}/invoice`);

        if (response.ok) {
            if (result !== null && result !== '') {
                setInvoices(result);
            }
        } else {
            console.log('getInvoices Error:', result);
            window.alert('Error getting Invoices');
        }
    };

    const getStates = async () => {
        const result = await get('/v1.0/leases/states');

        if (response.ok && Array.isArray(result)) {
            setStateOptions(result.map((state: { stCode: string; abbr: string }) => ({ value: state.stCode, label: state.abbr })));
        }
    };

    const getCounties = async (stateCode: string) => {
        if (!stateCode || countiesMap[stateCode]) {
            return;
        }

        const result = await get(`/v1.0/leases/states/${stateCode}/counties`);

        if (response.ok && Array.isArray(result)) {
            setCountiesMap((current) => ({
                ...current,
                [stateCode]: result.map((county: { county: string }) => ({ value: county.county, label: county.county })),
            }));
        }
    };

    const createLeaseMaster = async () => {
        let result = await post(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/processingMaintenance/lease/${props.leaseNumber}`);

        if (response.ok) {
            if (result > 0) {
                getLeaseMaster();
            } else {
                console.log('createLeaseMaster Error:', result);
                window.alert('Error: No master lease record found. Attempted creation of new lease record failed.');
            }
        } else {
            console.log('createLeaseMaster Error:', result);
            window.alert('Error creating Lease');
        }
    };

    const createLeaseRecording = async (recording: LeaseRecording) => {
        let result = await post(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/processingMaintenance/lease-recording`, recording);

        if (response.ok) {
            props.sendUpdate();
            props.onCloseDialog();
        } else {
            console.log('createLeaseRecording Error:', result);
            window.alert('Error creating Lease Recording');
        }
    };

    const updateLeaseRecording = async (recording: LeaseRecording) => {
        let result = await put(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/processingMaintenance/lease-recording`, recording);

        if (response.ok) {
            props.sendUpdate();
            props.onCloseDialog();
        } else {
            console.log('updateLeaseRecording Error:', result);
            window.alert('Error updating Lease Recording');
        }
    };

    const finalizeData = (data: LeaseRecording) => {
        data.assign_no = props.leaseRecording?.assign_no ? parseInt(props.leaseRecording?.assign_no + '', 10) : null;
        data.transmit_no = props.leaseRecording?.transmit_no ? parseInt(props.leaseRecording?.transmit_no + '', 10) : null;

        data.lr_tg = props.leaseRecording ? props.leaseRecording.lr_tg : 0;
        data.li_tg = data.lease_invoice?.li_tg ? parseInt(data.lease_invoice.li_tg + '', 10) : 0;

        data.rec_date = data.rec_date ? (moment(data.rec_date).format('YYYY-MM-DD') as unknown as Date) : null;
        data.assign_date = data.assign_date ? (moment(data.assign_date).format('YYYY-MM-DD') as unknown as Date) : null;
        data.tran_date = data.tran_date ? (moment(data.tran_date).format('YYYY-MM-DD') as unknown as Date) : null;

        data.rec_fee = data.rec_fee ? parseFloat(data.rec_fee + '') : 0;

        data.check_no = data.check_no ? parseInt(data.check_no + '', 10) : null;
        data.assign_order = data.assign_order ? parseInt(data.assign_order + '', 10) : null;
        data.recording_status = data.recording_status ? parseInt(data.recording_status + '', 10) : null;

        if (data.lease_invoice != null) {
            data.lease_invoice = finalizeInvoice(data.lease_invoice);
        }

        if (data.lease_lessor != null) {
            data.lease_lessor.ls_no = data.lease_lessor.ls_no ? parseInt(data.lease_lessor.ls_no + '', 10) : 0;
        }

        if (data.recording_detail != null) {
            data.recording_detail.id = data.recording_detail.id ? parseInt(data.recording_detail.id + '', 10) : 0;
            data.recording_detail.lr_tg = data.recording_detail.lr_tg ? parseInt(data.recording_detail.lr_tg + '', 10) : 0;
        }

        return data;
    };

    const finalizeInvoice = (data: LeaseInvoice) => {
        let invoice = invoices.find(i => i.li_tg === parseInt(data.li_tg + '', 10));
        if (invoice !== undefined) {
            data.li_tg = invoice.li_tg;
            data.invoice_add = invoice.invoice_add;
        } else {
            data.li_tg = 0;
            data.invoice_add = '';
        }

        data.ls_no = props.leaseNumber;

        data.adv_rental = data.adv_rental ? parseFloat(data.adv_rental + '') : 0;
        data.bank1 = data.bank1 ? parseFloat(data.bank1 + '') : 0;
        data.bonus1 = data.bonus1 ? parseFloat(data.bonus1 + '') : 0;
        data.other = data.other ? parseFloat(data.other + '') : 0;
        data.rec_fee = data.rec_fee ? parseFloat(data.rec_fee + '') : 0;
        data.tot_bonus = data.tot_bonus ? parseFloat(data.tot_bonus + '') : 0;
        
        data.due_date1 = data.due_date1 ? (moment(data.due_date1).format('YYYY-MM-DD') as unknown as Date) : null;

        data.invoice_no1 = data.invoice_no1 ? parseInt(data.invoice_no1 + '', 10) : null;
        data.ls_no = data.ls_no ? parseInt(data.ls_no + '', 10) : null;

        return data;
    };

    const onSubmit = (data: LeaseRecording) => {
        data = finalizeData(data);
        if (data.lr_tg > 0) {
            updateLeaseRecording(data);
        } else {
            createLeaseRecording(data);
        }
    };

    const updateInvoiceForm = (e: React.FormEvent<HTMLSelectElement>) => {
        let value = e.currentTarget.value;
        let fCurrent = getValues();
        let invoice = invoices.find(i => i.li_tg?.toString() === value);
        if (invoice === undefined) {
            invoice = new LeaseInvoice();
        }
        fCurrent.lease_invoice = invoice;
        reset(fCurrent);
    };

    const updateStateForm = (e: React.FormEvent<HTMLSelectElement>) => {
        const state = e.currentTarget.value;
        setValue('state', state);
        setValue('county', '');
        getCounties(state);
    };

    const bookOptions = () => {
        return [{ value: 'Y', label: 'Book/Page' }, { value: 'N', label: 'Instrument No.' }];
    };

    const statusOptions = () => {
        return [
            { value: 1, label: 'To Be Recorded'},
            { value: 2, label: 'To Be Transmitted' }, 
            { value: 3, label: 'To Be Assigned' },
            { value: 4, label: 'Recorded' },
            { value: 5, label: 'Assigned'},
            { value: 6, label: 'Transmitted'}];
    };

    return (
        <Dialog title="Lease Records">
            <div className="mb-1 mt-1" style={{ width: '85vw' }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div style={{ display: 'none' }}>
                        <RHFInputBox
                            name="recording_detail.id"
                            label=""
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            name="recording_detail.lr_tg"
                            label=""
                            register={register}
                            errors={errors}
                        />
                    </div>
                    <RHFLegend title="Lease Recording Info"/>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around', rowGap: '1rem' }}>
                        <RHFSelect
                            name="li_tg"
                            label="Invoice Number"
                            options={invoiceOptions}
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '10%' }}
                            style={{ width: '100%' }}
                            onChange={(e) => updateInvoiceForm(e)}
                        />

                        <RHFInputBox
                            name="lease_lessor.ls_no"
                            label="Lease No"
                            register={register}
                            errors={errors}
                            disabled={true}
                            bodyStyle={{ width: '10%' }}
                            style={{ width: '100%' }}
                        />

                        <RHFInputBox
                            name="lease_lessor.name1"
                            label="Lessor Name"
                            register={register}
                            errors={errors}
                            disabled={true}
                            bodyStyle={{ width: '10%' }}
                            style={{ width: '100%' }}
                        />

                        <RHFSelect
                            name="state"
                            label="State"
                            options={stateOptions}
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '5%' }}
                            style={{ width: '100%' }}
                            onChange={(e) => updateStateForm(e)}
                        />
                        <RHFSelect
                            name="county"
                            label="County"
                            options={selectedState && countiesMap[selectedState] ? countiesMap[selectedState] : []}
                            register={register}
                            errors={errors}
                            disabled={!selectedState}
                            bodyStyle={{ width: '10%' }}
                            style={{ width: '100%' }}
                        />

                        <RHFSelect
                            label="Instrument Type"
                            name="use_book"
                            options={bookOptions()}
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '10%' }}
                            style={{ width: '100%' }}
                        />

                        <RHFInputBox
                            name="book"
                            label="Book/Doc No."
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '8%' }}
                            style={{ width: '100%' }}
                        />

                        <RHFInputBox
                            name="page"
                            label="Page"
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '10%' }}
                            style={{ width: '100%' }}
                        />

                        <RHFInputBox
                            name="assign_no"
                            label="Asgn No."
                            register={register}
                            errors={errors}
                            disabled={true}
                            bodyStyle={{ width: '5%' }}
                            style={{ width: '100%' }}
                        />

                        <RHFInputBox
                            name="assign_order"
                            label="Asgn Order"
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '5%' }}
                            style={{ width: '100%' }}
                        />

                        <RHFSelect
                            label="Status"
                            name="recording_status"
                            options={statusOptions()}
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '10%' }}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: '1rem' }}>
                        <RHFTextArea
                            name="recording_detail.short_legal"
                            label="Short Legal Description"
                            register={register}
                            style={{ width: '100%', maxWidth: '100%', height: '10rem' }}
                            bodyStyle={{ width: '48%'}}
                        />
                    </div>
                    <br />
                    <RHFLegend title="Invoice Data"/>
                    <div style={{ display: 'none' }}>
                        <RHFInputBox
                            name="lease_invoice.invoice_no1"
                            register={register}
                            errors={errors}
                            disabled={true}
                        />
                        <RHFInputBox
                            name="lease_invoice.li_tg"
                            register={register}
                            errors={errors}
                            disabled={true}
                        />
                        <RHFInputBox
                            name="lease_invoice.invoice_add"
                            register={register}
                            errors={errors}
                            disabled={true}
                        />
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-around', rowGap: '1rem' }}>
                        <RHFInputBox
                            name="lease_invoice.collection1"
                            label="Collect No"
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '10%' }}
                            style={{ width: '100%' }}
                        />

                        <RHFInputBox
                            name="lease_invoice.due_date1"
                            label="Draft Due Date"
                            register={register}
                            errors={errors}
                            type="date"
                            bodyStyle={{ width: '10%' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            name="lease_invoice.bonus1"
                            label="Bonus Amount"
                            register={register}
                            errors={errors}
                            type="number"
                            bodyStyle={{ width: '10%' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            name="lease_invoice.adv_rental"
                            label="Rent Amount"
                            register={register}
                            errors={errors}
                            type="number"
                            bodyStyle={{ width: '10%' }}
                            style={{ width: '100%' }}
                        />

                        <RHFInputBox
                            name="lease_invoice.rec_fee"
                            label="Recording Fee"
                            register={register}
                            errors={errors}
                            type="number"
                            bodyStyle={{ width: '10%' }}
                            style={{ width: '100%' }}
                        />

                        <RHFInputBox
                            name="lease_invoice.bank1"
                            label="Bank Charge"
                            register={register}
                            errors={errors}
                            type="number"
                            bodyStyle={{ width: '10%' }}
                            style={{ width: '100%' }}
                        />

                        <RHFInputBox
                            name="lease_invoice.other"
                            label="Other"
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '10%' }}
                            style={{ width: '100%' }}
                        />

                        <RHFInputBox
                            name="lease_invoice.tot_bonus"
                            label="Total Bonus"
                            register={register}
                            errors={errors}
                            type="number"
                            bodyStyle={{ width: '10%' }}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <DialogActionBar>
                        <Button
                            intent="primary"
                            type="submit"
                            text="Save"
                        />
                        <Button intent="danger" onClick={() => props.onCloseDialog()} text="Cancel" />
                    </DialogActionBar>
                </form>
            </div>
        </Dialog>
    );
};

export default LeaseRecordDialog;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/ProcessingAndMaintenance/LeaseRecordDialog.tsx